// VirtualizedAutocomplete.tsx
import React, { useRef, useEffect, useMemo, useCallback } from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { FixedSizeList, ListChildComponentProps } from "react-window";

const LISTBOX_PADDING = 8;

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: (style.top as number) + LISTBOX_PADDING,
        },
    });
}

const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
        const { children, ...other } = props;
        const itemData = React.Children.toArray(children);
        const itemCount = itemData.length;
        const itemSize = 36;
        return (
            <div
                ref={ref}
                {...other}
                style={{ overflow: "hidden"}} // <-- Prevent double scrollbar
            >
                <FixedSizeList
                    height={400}
                    width="100%"
                    itemSize={itemSize}
                    itemCount={itemCount}
                    overscanCount={5}
                    itemData={itemData}
                    style={{ overflowY: "auto", maxHeight: '40vh' }}
                >
                    {renderRow}
                </FixedSizeList>
            </div>
        );
    }
);

// Omit renderInput from AutocompleteProps so it's only handled by us
type DtAutocompleteProps<
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = true,
    FreeSolo extends boolean | undefined = false
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> & {
    textFieldProps?: TextFieldProps;
    renderInput?: (params: any) => React.ReactNode;
};

function defaultGetOptionLabel(option: any) {
    if (typeof option === "string") return option;
    if (typeof option === "number") return option.toString();
    if (option?.label) return String(option.label);
    if (option?.name) return String(option.name);
    if (option?.value) return String(option.value);
    return "";
}

function DtAutocomplete<T>(props: DtAutocompleteProps<T>) {
    const { textFieldProps, renderInput, getOptionLabel, ...autocompleteProps } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current && inputRef.current instanceof HTMLInputElement) {
            inputRef.current?.removeAttribute("autocomplete");
        }
    }, []);

    return (
        <Autocomplete
            disablePortal={false} 
            ListboxComponent={VirtualizedListboxComponent}
            disableClearable
            getOptionLabel={getOptionLabel ?? defaultGetOptionLabel}
            {...autocompleteProps}
            renderInput={
                renderInput
                    ? (params) => renderInput({ ...params })
                    : (params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            label={textFieldProps?.label}
                            size={textFieldProps?.size ?? "small"}
                            variant={textFieldProps?.variant ?? "outlined"}
                            fullWidth={textFieldProps?.fullWidth ?? true}
                            {...textFieldProps}
                        />
                    )
            }
        />
    );
}

export { DtAutocomplete };